import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { applyUserSettings } from '../userSettingsSlice';
import { updateCameraForFlippedAndReversedSettings } from './flipAndReverse';
import {
  CameraProperties,
  CameraType,
  DesignCameraSettings,
  DesignCameraType,
  getRenderModeForCameraType,
  LayoutCameraType,
  RenderMode,
} from './types';

export interface CameraState {
  readonly mode: RenderMode;

  readonly activeDesignCamera: DesignCameraType;
  readonly activeLayoutCamera: LayoutCameraType;

  readonly properties: {
    // A null camera indicates that the camera has not been initialized yet. We will set the camera properties in state
    //   from the actual camera object once it is created.
    [type in CameraType]: CameraProperties | null;
  };

  readonly designSettings: DesignCameraSettings;
}

const INITIAL_STATE: CameraState = {
  mode: 'design',

  activeDesignCamera: 'orthographic-design',
  activeLayoutCamera: 'orthographic-layout',

  properties: {
    'orthographic-layout': null,
    'perspective-layout': null,
    'orthographic-design': null,
  },

  designSettings: {
    flipped: false,
    reversed: false,
  },
};

export const cameraSlice = createSlice({
  name: 'camera',
  initialState: INITIAL_STATE,
  reducers: {
    activeCameraChanged: (state, action: PayloadAction<CameraType>) => {
      state.mode = getRenderModeForCameraType(action.payload);

      switch (state.mode) {
        case 'design':
          state.activeDesignCamera = action.payload as DesignCameraType;
          break;

        case 'layout':
          state.activeLayoutCamera = action.payload as LayoutCameraType;
          break;

        default:
          assertExhaustiveSwitch(state.mode);
      }
    },

    cameraAltered: (state, action: PayloadAction<{ type: CameraType; properties: CameraProperties }>) => {
      state.properties[action.payload.type] = action.payload.properties;
    },

    designCameraFlipped: (state) => {
      state.designSettings.flipped = !state.designSettings.flipped;

      const orthographicDesignCamera = state.properties['orthographic-design'];
      if (orthographicDesignCamera) {
        updateCameraForFlippedAndReversedSettings(orthographicDesignCamera, state.designSettings);
      }
    },

    designCameraReversed: (state) => {
      state.designSettings.reversed = !state.designSettings.reversed;

      const orthographicDesignCamera = state.properties['orthographic-design'];
      if (orthographicDesignCamera) {
        updateCameraForFlippedAndReversedSettings(orthographicDesignCamera, state.designSettings);
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(applyUserSettings, (state, action) => {
      const { cameraSettings } = action.payload;

      state.designSettings.flipped = cameraSettings.flipped;
      state.designSettings.reversed = cameraSettings.reversed;

      const orthographicDesignCamera = state.properties['orthographic-design'];
      if (orthographicDesignCamera) {
        updateCameraForFlippedAndReversedSettings(orthographicDesignCamera, cameraSettings);
      }
    });
  },
});
